import { Icon } from 'Components';
import { Review, LearningPath } from 'LearningManager/pages/QuickSetup';
import {
	AssignUsers,
	Review as CustomReview,
	SelectCourses
} from 'LearningManager/pages/CustomSetup';
import course1 from 'LearningManager/images/course-1.png';
import course2 from 'LearningManager/images/course-2.png';
import course3 from 'LearningManager/images/course-3.jpg';
import course4 from 'LearningManager/images/course-4.jpg';
import React from 'react';
import moment from 'moment';
import Organization from 'LearningManager/components/AssignUsers/Organisation';
import Property from 'LearningManager/components/AssignUsers/Property';
import Role from 'LearningManager/components/AssignUsers/Role';
import Group from 'LearningManager/components/AssignUsers/Group';
import Individual from 'LearningManager/components/AssignUsers/Individual';
import Configure from 'LearningManager/pages/Configure';

export type AssignmentSetupType = 'Quick Setup' | 'Custom Setup';
export interface Step {
	id: number;
	title: string;
	component: React.ComponentType;
}
export interface SetupProps {
	title: string;
	translationKey: string;
	icon: React.ReactNode;
	selectedIcon?: React.ReactNode;
	subTitle: string;
	listTitle: string;
	list: string[];
	isRecommended?: boolean;
	isActive?: boolean;
	onClick?: () => void;
}

export interface Language {
	label: string;
}

export interface Course {
	id: string;
	number: number;
	title: string;
	subtitle: string;
	description: string;
	runTime: string;
	created: string;
	languages: Language[];
}

export interface CourseDetailModalProps {
	title: string;
	courseId: string;
	description: string;
	runTime: string;
	created: string;
	languages: Language[];
	courses: Course[];
}

export const SetupOptions: SetupProps[] = [
	{
		title: 'Quick Setup',
		translationKey: 'learning.createAssignment.setupOptions.quickSetup',
		icon: <Icon type="quickSetupIcon" role="img" />,
		selectedIcon: <Icon type="quickSetupFilled" role="img" />,
		subTitle: "Get started fast with Venza's proven setup",
		listTitle: 'Default assignments:',
		list: [
			'Staff - Basic Learning Path',
			'Managers - Basic + Intermediate Learning Path',
			'Leadership & IT - Basic + Intermediate + Advanced Learning Path'
		],
		isRecommended: true
	},
	{
		title: 'Custom Setup',
		translationKey: 'learning.createAssignment.setupOptions.customSetup',
		icon: <Icon type="customSetupIcon" role="img" />,
		subTitle: 'Build a tailored curriculum with full control',
		listTitle: 'You can customize:',
		list: [
			'Individual courses and learning paths',
			'Properties, groups & users',
			'Status, assignments type and more'
		]
	}
];

export const quickSetupSteps: Step[] = [
	{
		id: 1,
		title: 'learning.createAssignment.steps.learningPathReview',
		component: LearningPath
	},
	{
		id: 2,
		title: 'learning.createAssignment.steps.configureSettings',
		component: Configure
	},
	{
		id: 3,
		title: 'learning.createAssignment.steps.reviewConfirm',
		component: Review
	}
];

export const customSetupSteps: Step[] = [
	{
		id: 1,
		title: 'learning.createAssignment.steps.selectCourses',
		component: SelectCourses
	},
	{
		id: 2,
		title: 'learning.createAssignment.steps.assignUsers',
		component: AssignUsers
	},
	{
		id: 3,
		title: 'learning.createAssignment.steps.configureSettings',
		component: Configure
	},
	{
		id: 4,
		title: 'learning.createAssignment.steps.reviewConfirm',
		component: CustomReview
	}
];

export const getStepsByType = (type: AssignmentSetupType): Step[] =>
	type === 'Quick Setup' ? quickSetupSteps : customSetupSteps;

// Editing a started assignment: only Configure + Review, no course/user selection
export const getLimitedEditSteps = (type: AssignmentSetupType): Step[] => [
	{
		id: 1,
		title: 'learning.createAssignment.steps.configureSettings',
		component: Configure
	},
	{
		id: 2,
		title: 'learning.createAssignment.steps.reviewConfirm',
		component: type === 'Quick Setup' ? Review : CustomReview
	}
];

export const learningPaths = [
	{
		title: 'learning.createAssignment.learningPaths.basic.title',
		duration: 'learning.createAssignment.learningPaths.basic.duration',
		description:
			'learning.createAssignment.learningPaths.basic.description',
		courses: [
			{
				id: 1,
				title: 'learning.createAssignment.learningPaths.basic.courses.allAboutData',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course1
			},
			{
				id: 2,
				title: 'learning.createAssignment.learningPaths.basic.courses.dataThreats',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course2
			},
			{
				id: 3,
				title: 'learning.createAssignment.learningPaths.basic.courses.protectingData',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course3
			}
		]
	},
	{
		title: 'learning.createAssignment.learningPaths.intermediate.title',
		duration: 'learning.createAssignment.learningPaths.intermediate.duration',
		description:
			'learning.createAssignment.learningPaths.intermediate.description',
		courses: [
			{
				id: 1,
				title: 'learning.createAssignment.learningPaths.intermediate.courses.informationSecurityBreach',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course4
			},
			{
				id: 2,
				title: 'learning.createAssignment.learningPaths.intermediate.courses.bookingHotelScam',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course3
			},
			{
				id: 3,
				title: 'learning.createAssignment.learningPaths.intermediate.courses.urgentCEORequest',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course2
			},
			{
				id: 4,
				title: 'learning.createAssignment.learningPaths.intermediate.courses.urgentCEORequest',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course1
			}
		]
	},
	{
		title: 'learning.createAssignment.learningPaths.advanced.title',
		duration: 'learning.createAssignment.learningPaths.advanced.duration',
		description:
			'learning.createAssignment.learningPaths.advanced.description',
		courses: [
			{
				id: 1,
				title: 'learning.createAssignment.learningPaths.advanced.courses.dataSecurity',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course4
			},
			{
				id: 2,
				title: 'learning.createAssignment.learningPaths.advanced.courses.compliance',
				tag: 'learning.createAssignment.learningPaths.tag',
				image: course3
			}
		]
	}
];

export const courseDetailMockData: CourseDetailModalProps = {
	title: 'Fake Login Page',
	courseId: '1234',
	description:
		'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
	runTime: '60 minutes',
	created: '2024',
	languages: [
		{ label: 'English' },
		{ label: 'Spanish' },
		{ label: 'Chinese' },
		{ label: 'Italian' },
		{ label: 'French' },
		{ label: 'German' }
	],
	courses: [
		{
			id: '1',
			number: 1,
			title: 'Information security breaches',
			subtitle: 'Security awareness',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '2',
			number: 2,
			title: 'Information security policies',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '3',
			number: 3,
			title: 'PCI DSS Expectations for leaders',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		},
		{
			id: '4',
			number: 4,
			title: 'Vulnerability and endor management',
			subtitle: 'AI-Driven',
			description:
				'An AI-powered phishing simulation that creates realistic fake login pages to test user awareness harvesting attempts.',
			runTime: '20 minutes',
			created: '2024',
			languages: [
				{ label: 'English' },
				{ label: 'Spanish' },
				{ label: 'Chinese' },
				{ label: 'Italian' },
				{ label: 'French' },
				{ label: 'German' }
			]
		}
	]
};

export interface OptionProps {
	id: number;
	icon: React.ReactNode;
	selectedIcon: React.ReactNode;
	title: string;
	desc: string;
	component: React.ReactNode;
}
export interface OptionCardProps extends OptionProps {
	selectedId: number | null;
	toggleSelect: (id: number) => void;
}

// Assign Users option ids, as stored on the draft and sent as assignment scopes.
export const ASSIGN_USERS_OPTION = {
	ORGANIZATION: 214,
	PROPERTY: 35,
	ROLE: 634,
	GROUP: 676,
	INDIVIDUAL: 784
} as const;

export const options: OptionProps[] = [
	{
		id: ASSIGN_USERS_OPTION.ORGANIZATION,
		icon: <Icon type="orgOptIcon" />,
		selectedIcon: <Icon type="orgOptFilled" />,
		title: 'learning.assignUsers.options.organization.title',
		desc: 'learning.assignUsers.options.organization.desc',
		component: <Organization />
	},
	{
		id: ASSIGN_USERS_OPTION.PROPERTY,
		icon: <Icon type="propOptIcon" />,
		selectedIcon: <Icon type="propOptFilled" />,
		title: 'learning.assignUsers.options.property.title',
		desc: 'learning.assignUsers.options.property.desc',
		component: <Property />
	},
	{
		id: ASSIGN_USERS_OPTION.ROLE,
		icon: <Icon type="roleOptIcon" />,
		selectedIcon: <Icon type="roleOptFilled" />,
		title: 'learning.assignUsers.options.role.title',
		desc: 'learning.assignUsers.options.role.desc',
		component: <Role />
	},
	{
		id: ASSIGN_USERS_OPTION.GROUP,
		icon: <Icon type="groupOptIcon" />,
		selectedIcon: <Icon type="groupOptFilled" />,
		title: 'learning.assignUsers.options.group.title',
		desc: 'learning.assignUsers.options.group.desc',
		component: <Group />
	},
	{
		id: ASSIGN_USERS_OPTION.INDIVIDUAL,
		icon: <Icon type="individualOptIcon" />,
		selectedIcon: <Icon type="indiOptFilled" />,
		title: 'learning.assignUsers.options.individual.title',
		desc: 'learning.assignUsers.options.individual.desc',
		component: <Individual />
	}
];

export interface ConfigureState {
	assignmentName: string;
	certDuration: string;
	certExpirationDate: string;
	assignmentStart: string;
	assignmentStartDate: string;
	dueDate: string;
	dueDateValue: string;
	assignmentType: string;
}

// Whole option objects are stored (not just ids) so a selection can be shown
// anywhere without re-querying the fetched lists.
export interface SelectedGroupOption {
	id: number;
	name: string;
	label?: string;
}

export interface SelectedRoleOption {
	id: number;
	name: string;
}

export interface SelectedUserOption {
	id: string; // UUID
	firstName: string;
	lastName: string;
	email: string;
}

export type SelectedAudienceOption =
	| SelectedGroupOption
	| SelectedRoleOption
	| SelectedUserOption;

// Audience fields that hold plain option objects (users are toggled separately).
export type AssignUsersOptionField =
	| 'selectedOrganizations'
	| 'selectedProperties'
	| 'selectedRoles'
	| 'selectedGroups';

export type AssignUsersSelectionField =
	| AssignUsersOptionField
	| 'selectedIndividualUsers';

export interface CustomSetupState {
	selectedLearningPathCourseIds: number[];
	selectedIndividualCourseIds: number[];
	optionalLearningPathIds: number[];
	optionalIndividualCourseIds: number[];
	selectedAssignUsersOptionId: number | null;
	selectedOrganizations: SelectedGroupOption[];
	selectedProperties: SelectedGroupOption[];
	selectedRoles: SelectedRoleOption[];
	selectedRolePropertyIds: number[];
	allRolePropertiesSelected: boolean;
	selectedGroups: SelectedGroupOption[];
	selectedGroupPropertyIds: number[];
	allGroupPropertiesSelected: boolean;
	selectedIndividualUsers: SelectedUserOption[];
	selectedIndividualPropertyIds: number[];
	allIndividualPropertiesSelected: boolean;
}

export interface QuickSetupState {
	selectedLearningPathCourseIds: number[];
}

export interface AssignmentDraft {
	configure: ConfigureState;
	quickSetup: QuickSetupState;
	customSetup: CustomSetupState;
}

// Which draft field each Assign Users option fills.
export const ASSIGN_USERS_SELECTION_FIELD: Partial<
	Record<number, AssignUsersSelectionField>
> = {
	[ASSIGN_USERS_OPTION.ORGANIZATION]: 'selectedOrganizations',
	[ASSIGN_USERS_OPTION.PROPERTY]: 'selectedProperties',
	[ASSIGN_USERS_OPTION.ROLE]: 'selectedRoles',
	[ASSIGN_USERS_OPTION.GROUP]: 'selectedGroups',
	[ASSIGN_USERS_OPTION.INDIVIDUAL]: 'selectedIndividualUsers'
};

export const DEFAULT_DRAFT: AssignmentDraft = {
	configure: {
		assignmentName: '',
		certDuration: '',
		certExpirationDate: '',
		assignmentStart: '',
		assignmentStartDate: '',
		dueDate: '',
		dueDateValue: '',
		assignmentType: ''
	},
	quickSetup: {
		selectedLearningPathCourseIds: []
	},
	customSetup: {
		selectedLearningPathCourseIds: [],
		selectedIndividualCourseIds: [],
		optionalLearningPathIds: [],
		optionalIndividualCourseIds: [],
		selectedAssignUsersOptionId: null,
		selectedOrganizations: [],
		selectedProperties: [],
		selectedRoles: [],
		selectedRolePropertyIds: [],
		allRolePropertiesSelected: false,
		selectedGroups: [],
		selectedGroupPropertyIds: [],
		allGroupPropertiesSelected: false,
		selectedIndividualUsers: [],
		selectedIndividualPropertyIds: [],
		allIndividualPropertiesSelected: false
	}
};

// The options picked under the chosen Assign Users method.
export const getAssignUsersSelection = (
	draft: AssignmentDraft
): SelectedAudienceOption[] => {
	const field =
		ASSIGN_USERS_SELECTION_FIELD[
			draft.customSetup.selectedAssignUsersOptionId ?? -1
		];
	return field ? draft.customSetup[field] : [];
};

// Per-step completeness: what the Next button and a restored draft both go by.
// A custom certification duration isn't complete until its date is picked.
export const hasCertDuration = (draft: AssignmentDraft): boolean =>
	draft.configure.certDuration === 'custom'
		? Boolean(draft.configure.certExpirationDate)
		: Boolean(draft.configure.certDuration);

export const hasAssignmentStart = (draft: AssignmentDraft): boolean =>
	draft.configure.assignmentStart === 'schedule'
		? Boolean(draft.configure.assignmentStartDate)
		: Boolean(draft.configure.assignmentStart);

export const hasDueDate = (draft: AssignmentDraft): boolean =>
	draft.configure.dueDate === 'custom'
		? Boolean(draft.configure.dueDateValue)
		: Boolean(draft.configure.dueDate);

export const DUE_DATE_BEFORE_START_ERROR = 'learning.createAssignment.errors.dueDateBeforeStart';

export const getAssignmentStartDate = (
	configure: ConfigureState
): string | null =>
	configure.assignmentStart === 'schedule' && configure.assignmentStartDate
		? configure.assignmentStartDate
		: null;

export const CERT_EXPIRATION_BEFORE_START_ERROR =
	'learning.createAssignment.errors.certExpirationBeforeStart';

const isNotAfterStart = (
	date: string,
	configure: ConfigureState,
	isEditMode: boolean
): boolean => {
	const startDate =
		(isEditMode && configure.assignmentStartDate) ||
		getAssignmentStartDate(configure) ||
		moment().format('YYYY-MM-DD');
	return !moment.utc(date).isAfter(moment.utc(startDate), 'day');
};

export const isDueDateBeforeStart = (
	configure: ConfigureState,
	isEditMode = false
): boolean =>
	configure.dueDate === 'custom' &&
	Boolean(configure.dueDateValue) &&
	isNotAfterStart(configure.dueDateValue, configure, isEditMode);

// Edit mode is skipped: the stored expiry is 'MM-DD', so its year is a guess.
export const isCertExpirationBeforeStart = (
	configure: ConfigureState,
	isEditMode = false
): boolean =>
	!isEditMode &&
	configure.certDuration === 'custom' &&
	Boolean(configure.certExpirationDate) &&
	isNotAfterStart(configure.certExpirationDate, configure, isEditMode);

export const hasConfigureData = (
	draft: AssignmentDraft,
	isEditMode = false
): boolean =>
	Boolean(
		draft.configure.assignmentName.trim() &&
			hasCertDuration(draft) &&
			(isEditMode || hasAssignmentStart(draft)) &&
			hasDueDate(draft) &&
			draft.configure.assignmentType
	) &&
	!isDueDateBeforeStart(draft.configure, isEditMode) &&
	!isCertExpirationBeforeStart(draft.configure, isEditMode);

export const hasCustomSelection = (draft: AssignmentDraft): boolean =>
	draft.customSetup.selectedLearningPathCourseIds.length > 0 ||
	draft.customSetup.selectedIndividualCourseIds.length > 0;

export const hasAssignUsersSelection = (draft: AssignmentDraft): boolean =>
	getAssignUsersSelection(draft).length > 0;

// The furthest step a draft may resume at — the first step still missing data,
// indexed into that setup type's own step list.
export const getFurthestAllowedStep = (
	setupType: AssignmentSetupType,
	draft: AssignmentDraft
): number => {
	if (setupType === 'Quick Setup') {
		return hasConfigureData(draft) ? 2 : 1;
	}

	if (!hasCustomSelection(draft)) return 0;
	if (!hasAssignUsersSelection(draft)) return 1;
	if (!hasConfigureData(draft)) return 2;
	return 3;
};
